/**
* Nom : documentList.js
* Description : Ce fichier contient la définition de la page documentList 
*               Cette page liste la documentation des produits avec un filtrage
* Date de création : 23 septembre 2025
*/

/* CSS et assets */
import { EditIcon } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import { TabC } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { Item } from 'package_ui_ihm/dist'
import { ListFilter } from 'package_ui_ihm/dist'
import Divider from '@mui/material/Divider';
import { BackPage } from 'package_ui_ihm/dist'
import { GotoPage } from 'package_ui_ihm/dist'
import { AddItem } from 'package_ui_ihm/dist'

/* Hook */
import { useState, useEffect } from 'react';
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useScreenWidth } from 'package_ui_ihm/dist'
import { useNavigate } from 'react-router-dom'


/**
* DocumentList : affiche la page qui liste la documentation des produits
*                filtrage de la liste possible selon le produit et la version
*                un lien permet d'afficher un formulaire pour créer un nouveau document
*                pour chaque document une icone permet de l'éditer
*
* Type : page 
*
* Parametres : 
*  None
*/
function DocumentList() {

  // Define useState hooks

  const [documents, setDocuments] = useState([])
  const [statusDocument, setStatusDocument] = useState(null)
  const [isDataLoading, setDataLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [products, setProducts] = useState([])
  const [selectedDocumentType, setSelectedDocumentType] = useState(0);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [versions, setVersions] = useState(null);
  const [filteredDocuments, setFilteredDocuments] = useState(documents); 


  // Define useContext hooks
  const { authStatus, authToken, currentUser } = useAuth();
  const { path } = useApi();

  const navigate = useNavigate();


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();
  

  // Define functions
  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
        accept: 'application/json',
        Authorization: token,
    }
    // Requests in parallel to win time 
    const [resProduct, resDocumentType, resDocument] = await Promise.all([
        // Products
        fetch(path + "/api/eq-prct/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Document Types
        fetch(path + "/api/eq-doty/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
        // Documents
        fetch(path + "/api/eq-dont/", {
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers,
        }),
    ])
    // Responses in parallel to win time
    const [dataProduct, dataDocumentType, dataDocument] = await Promise.all([
        resProduct.json(),
        resDocumentType.json(),
        resDocument.json(),
    ])
    // Set variables
    setProducts(dataProduct["results"])
    setDocumentTypes(dataDocumentType["results"])
    setStatusDocument(resDocument["status"])
    const document_list = dataDocument["results"]
    setDocuments(document_list)
    // Version des documents 
    const version_list = [...new Set(document_list.map((document) => (document.version)))]
    const version_dict = version_list.map((version, index) => ({"id": index + 1, "value": version}))
    setVersions(version_dict)
    // Dataloading
    setDataLoading(false)
  }

  const handleIcon = (event, id) => {
    event.preventDefault();
    navigate('/documents/' + id + '/edit')
  };

  function retrieve_param_object_from_id(list, id) {
    return (list.find((item) => (item['id'] === Number(id))))['value']
  }

  function updateFilteredDocuments() {
    // Filter product
    if( Number(selectedProduct) > 0 
        && Number(selectedDocumentType) === 0
        && Number(selectedVersion) === 0)
    {
      setFilteredDocuments(documents.filter((document) => document.product.id === Number(selectedProduct)))
    } 
    // Filter document type
    else if( Number(selectedProduct) === 0 
              && Number(selectedDocumentType) > 0
              && Number(selectedVersion) === 0)
    {
      setFilteredDocuments(documents.filter((document) => document.document_type === Number(selectedDocumentType)))
    } 
    // Filter version
    else if( Number(selectedProduct) === 0 
              && Number(selectedDocumentType) === 0
              && Number(selectedVersion) > 0)
    {
      setFilteredDocuments(documents.filter((document) => document.version === retrieve_param_object_from_id(versions, selectedVersion)))
    } 
    // Filter product and document type
    else if( Number(selectedProduct) > 0 
              && Number(selectedDocumentType) > 0
              && Number(selectedVersion) === 0)
    {
      setFilteredDocuments(documents.filter((document) => document.product.id === Number(selectedProduct) 
                                                        && document.document_type.id === Number(selectedDocumentType)))
    }
    // Filter product and version
    else if( Number(selectedProduct) > 0 
              && Number(selectedDocumentType) === 0
              && Number(selectedVersion) > 0)
    {
      setFilteredDocuments(documents.filter((document) => document.product.id === Number(selectedProduct) 
                                                        && document.version === retrieve_param_object_from_id(versions, selectedVersion)))
    } 
    // Filter document type and version
    else if( Number(selectedProduct) === 0 
              && Number(selectedDocumentType) > 0
              && Number(selectedVersion) > 0)
    {
      setFilteredDocuments(documents.filter((document) => document.document_type.id === Number(selectedDocumentType) 
                                                        && document.version === retrieve_param_object_from_id(versions, selectedVersion)))
    }
    // Filter product and document type and version
    else if( Number(selectedProduct) > 0 
              && Number(selectedDocumentType) > 0
              && Number(selectedVersion) > 0)
    {
      setFilteredDocuments(documents.filter((document) => document.product.id === Number(selectedProduct) 
                                                        && document.document_type.id === Number(selectedDocumentType) 
                                                        && document.version === retrieve_param_object_from_id(versions, selectedVersion)))
    } 
    // No filter
    else
    {
      setFilteredDocuments(documents)
    }
  }

  const filterReset = (event) => {
      event.preventDefault();
      setFilteredDocuments(documents)
      setSelectedDocumentType(0)
      setSelectedProduct(0) 
      setSelectedVersion(0)
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateFilteredDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, selectedProduct, selectedDocumentType, selectedVersion])


  // Render component
  return (
    <div>
      <TabC tabNumber={1}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <p>Chargement des données</p>
          ) : (
            (statusDocument === 200) ? (
              <div>
                <h1>Documentation des produits</h1>  
                {/*Lien de retour*/}
                <div className='flex-row justify-center margin-bottom margin-top'>
                  <div className='link-border'>
                    <BackPage path='/equipments'>Retour à la liste des produits client</BackPage>
                  </div>
                  <div className='link-border'>
                    <GotoPage path='/document-types'>Gérer les types de documents</GotoPage>
                  </div>
                </div>
                <div className={(screenWidth > 650) ? 'grid-1-3' : null}>
                  {/*New*/}
                  <div className='flex-column'>
                    <AddItem path='/documents/new'>Ajouter un document</AddItem>
                    <h3>Filtres</h3>
                    <ListFilter labelText='Produit' choices={products.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedProduct} setSelectedItem={setSelectedProduct} subfilter={false}/>
                    <ListFilter labelText='Type de document' choices={documentTypes.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedDocumentType} setSelectedItem={setSelectedDocumentType} subfilter={false}/>
                    <ListFilter labelText='Version' choices={versions.sort((a, b) => (a.value > b.value)?1:-1)} choiceParam={'value'} selectedItem={selectedVersion} setSelectedItem={setSelectedVersion} subfilter={false}/>
                    <div>
                      <button onClick={filterReset} className='button-secondary clickable'>Sans filtres</button>
                    </div>
                  </div>
                  {(screenWidth > 650) ? null : <Divider/>}
                  {/*Document list*/}
                  <div className='scroll-list-rd'>
                    {(filteredDocuments && filteredDocuments.length !== 0) ? (
                      filteredDocuments.sort((a, b) => (a.product.name > b.product.name)?1:-1).map((document) => (
                        <Item key={document['id']} id={document['id']}>
                          <div className='grid-2-3-2-1'>
                            <p className='item-text'><strong>Produit :</strong> {document['product']['name']}</p>
                            <p className='item-text'><strong>Type de document :</strong> {document['document_type']['name']}</p>
                            <p className='item-text'><strong>Version :</strong> {document['version']}</p>
                            <div className='flex-row justify-end clickable'>
                              { (currentUser["role_name"] === "AT_ADMIN" || currentUser["role_name"] === "AT_SUPPORT") ? (
                                  <img src={EditIcon} alt='edit_item_icon' onClick={(e) => handleIcon(e, document['id']) } className='link-img'/>
                                ) : (
                                  null
                                )
                              }
                            </div>
                          </div>
                        </Item>
                      ))
                    ) : (
                      <p>Aucun document actuellement.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <h1>Connexion failed</h1>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default DocumentList;
